// src/pages/Tasks.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell.jsx";
import { apiFetchJson, getStoredUser } from "../api/api.js";

import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

/* =========================
   ENUMS + GROUPS
   ========================= */

const STATUS_UA = {
  PLANNED: "Заплановано",
  IN_PROGRESS: "В роботі",
  DONE: "Виконано",
  CANCELLED: "Скасовано",
  BLOCKED: "Заблоковано",
};

const CATEGORY_UA = {
  PLANNING: "Планування",
  SOIL: "Ґрунт",
  SEEDING: "Посів",
  NUTRITION: "Живлення",
  PROTECTION: "Захист",
  IRRIGATION: "Зрошення",
  HARVEST: "Збір",
  MAINTENANCE: "Обслуговування",
  INCIDENT: "Інцидент",
  OTHER: "Інше",
};

const TYPE_UA = {
  SCOUTING: "Огляд",
  SOIL_SAMPLING: "Відбір проб ґрунту",
  MAPPING: "Картування",
  DOCUMENTATION: "Документація",
  PLOWING: "Оранка",
  DISKING: "Дискування",
  CULTIVATION: "Культивація",
  HARROWING: "Боронування",
  ROLLING: "Коткування",
  SEEDING: "Посів",
  TRANSPLANTING: "Пересадка",
  RESEEDING: "Пересів",
  BASE_FERTILIZATION: "Основне внесення",
  TOPDRESS: "Підживлення",
  FOLIAR: "Позакореневе",
  HEERBICIDE: "Гербіцид",
  FUNGICIDE: "Фунгіцид",
  INSECTICIDE: "Інсектицид",
  DESICCATION: "Десикація",
  IRRIGATION: "Полив",
  FERTIGATION: "Фертигація",
  SYSTEM_CHECK: "Перевірка системи",
  HARVESTING: "Збирання",
  TRANSPORT: "Транспортування",
  DRYING: "Сушіння",
  EQUIPMENT_SERVICE: "Сервіс техніки",
  FUELING: "Заправка",
  WEEDS: "Бур'яни",
  DISEASE: "Хвороби",
  PESTS: "Шкідники",
  NUTRIENT_DEFICIENCY: "Дефіцит елементів",
  DAMAGE: "Пошкодження",
  OTHER: "Інше",
};

const DETAILS_STATUS_META = {
  PLANNED:     { label: "Заплановано",  icon: "🗓",  color: "#94a3b8", bg: "rgba(148,163,184,.15)", border: "rgba(148,163,184,.3)"  },
  IN_PROGRESS: { label: "В роботі",     icon: "⚡",  color: "#f59e0b", bg: "rgba(245,158,11,.15)",  border: "rgba(245,158,11,.35)"  },
  DONE:        { label: "Виконано",     icon: "✅",  color: "#22c55e", bg: "rgba(34,197,94,.15)",   border: "rgba(34,197,94,.35)"   },
  CANCELLED:   { label: "Скасовано",   icon: "🚫",  color: "#ef4444", bg: "rgba(239,68,68,.15)",   border: "rgba(239,68,68,.3)"    },
  BLOCKED:     { label: "Заблоковано", icon: "🔒",  color: "#f97316", bg: "rgba(249,115,22,.15)",  border: "rgba(249,115,22,.3)"   },
};

const CATEGORY_TO_TYPES = {
  PLANNING: ["SCOUTING", "SOIL_SAMPLING", "MAPPING", "DOCUMENTATION"],
  SOIL: ["PLOWING", "DISKING", "CULTIVATION", "HARROWING", "ROLLING"],
  SEEDING: ["SEEDING", "TRANSPLANTING", "RESEEDING"],
  NUTRITION: ["BASE_FERTILIZATION", "TOPDRESS", "FOLIAR"],
  PROTECTION: ["HEERBICIDE", "FUNGICIDE", "INSECTICIDE", "DESICCATION"],
  IRRIGATION: ["IRRIGATION", "FERTIGATION", "SYSTEM_CHECK"],
  HARVEST: ["HARVESTING", "TRANSPORT", "DRYING"],
  MAINTENANCE: ["EQUIPMENT_SERVICE", "FUELING"],
  INCIDENT: ["WEEDS", "DISEASE", "PESTS", "NUTRIENT_DEFICIENCY", "DAMAGE"],
  OTHER: ["OTHER"],
};

/* =========================
   THEME (CSS VARIABLES)
   ========================= */

const c = {
  bg: "var(--bg)",
  bg2: "var(--bg2)",
  panel: "var(--panel)",
  panel2: "var(--panel2)",
  text: "var(--text)",
  textDim: "var(--muted)",
  border: "var(--border)",
  borderStrong: "var(--border-strong)",
  accent: "var(--g)",
  accent2: "var(--g2)",
  accent3: "var(--g3)",
  danger: "var(--danger)",
  warn: "var(--warn)",
  surface: "var(--surface)",
  surfaceSoft: "var(--surface-soft)",
  surfaceSoft2: "var(--surface-soft-2)",
  surfaceHover: "var(--surface-hover)",
  surfaceActive: "var(--surface-active)",
  topbarBg: "var(--topbar-bg)",
  modalBg: "var(--modal-bg)",
  overlay: "var(--overlay)",
  modalOverlay: "var(--modal-overlay)",
  iconBg: "var(--icon-bg)",
  iconText: "var(--icon-text)",
  primaryBtnText: "var(--primary-btn-text)",
  primaryBtnBg: "var(--primary-btn-bg)",
  primaryBtnBorder: "var(--primary-btn-border)",
  primaryBtnShadow: "var(--primary-btn-shadow)",
  shadow: "var(--shadow)",
  shadowSoft: "var(--shadow-soft)",
  focus: "var(--focus)",
};

function pillStyle(kind) {
  if (kind === "ok") {
    return {
      border: "1px solid color-mix(in srgb, var(--g) 26%, transparent)",
      background: "color-mix(in srgb, var(--g) 12%, transparent)",
      color: "var(--g)",
    };
  }

  if (kind === "warn") {
    return {
      border: "1px solid color-mix(in srgb, var(--warn) 30%, transparent)",
      background: "color-mix(in srgb, var(--warn) 12%, transparent)",
      color: "var(--warn)",
    };
  }

  if (kind === "bad") {
    return {
      border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)",
      background: "color-mix(in srgb, var(--danger) 12%, transparent)",
      color: "var(--danger)",
    };
  }

  return {
    border: "1px solid var(--border)",
    background: "var(--surface-soft)",
    color: "var(--muted)",
  };
}

const ui = {
  panel: {
    border: `1px solid ${c.border}`,
    background: `linear-gradient(180deg, ${c.panel2}, ${c.panel})`,
    borderRadius: 18,
    padding: 14,
    boxShadow: c.shadow,
    backdropFilter: "blur(14px)",
  },

  btn: {
    height: 44,
    padding: "0 14px",
    borderRadius: 12,
    border: `1px solid ${c.border}`,
    background: `linear-gradient(180deg, ${c.surface}, ${c.surfaceSoft})`,
    color: c.text,
    cursor: "pointer",
    fontWeight: 900,
    whiteSpace: "nowrap",
    boxShadow: c.shadowSoft,
    transition: ".18s ease",
  },

  btnPrimary: {
    height: 44,
    padding: "0 14px",
    borderRadius: 12,
    border: `1px solid ${c.primaryBtnBorder}`,
    background: c.primaryBtnBg,
    color: c.primaryBtnText,
    cursor: "pointer",
    fontWeight: 950,
    whiteSpace: "nowrap",
    boxShadow: c.primaryBtnShadow,
    transition: ".18s ease",
  },

  btnDanger: {
    height: 44,
    padding: "0 14px",
    borderRadius: 12,
    border: "1px solid color-mix(in srgb, var(--danger) 28%, transparent)",
    background: "color-mix(in srgb, var(--danger) 10%, transparent)",
    color: c.danger,
    cursor: "pointer",
    fontWeight: 950,
    whiteSpace: "nowrap",
    transition: ".18s ease",
  },

  input: {
    width: "100%",
    height: 48,
    borderRadius: 14,
    padding: "0 14px",
    border: `1px solid ${c.border}`,
    outline: "none",
    background: c.surface,
    color: c.text,
    fontSize: 16,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)",
  },

  textarea: {
    width: "100%",
    borderRadius: 14,
    padding: 14,
    border: `1px solid ${c.border}`,
    outline: "none",
    background: c.surface,
    color: c.text,
    resize: "vertical",
    minHeight: 110,
    fontSize: 16,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: c.modalOverlay,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 120,
    padding: 12,
  },

  modal: {
    width: "min(1240px, 98vw)",
    borderRadius: 18,
    overflow: "hidden",
    background: c.modalBg,
    border: `1px solid ${c.border}`,
    boxShadow: "0 26px 90px rgba(0,0,0,.22)",
    maxHeight: "94vh",
    display: "flex",
    flexDirection: "column",
    backdropFilter: "blur(16px)",
  },

  modalHeader: {
    padding: 14,
    borderBottom: `1px solid ${c.border}`,
    background: `linear-gradient(180deg, ${c.panel2}, ${c.panel})`,
    flex: "0 0 auto",
  },

  modalTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 950,
    letterSpacing: 0.2,
    color: c.text,
  },

  modalSub: {
    marginTop: 6,
    fontSize: 12,
    color: c.textDim,
    lineHeight: 1.35,
  },

  modalBody: {
    padding: 14,
    overflow: "auto",
    flex: "1 1 auto",
  },

  chip: (kind) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    padding: "5px 10px",
    borderRadius: 999,
    fontWeight: 950,
    whiteSpace: "nowrap",
    ...pillStyle(kind),
  }),

  card: {
    border: `1px solid ${c.border}`,
    borderRadius: 18,
    padding: 14,
    background: `linear-gradient(180deg, ${c.panel2}, ${c.panel})`,
    boxShadow: c.shadowSoft,
    backdropFilter: "blur(12px)",
  },
};

function statusTone(status) {
  if (status === "DONE") return "ok";
  if (status === "IN_PROGRESS") return "warn";
  if (status === "CANCELLED" || status === "BLOCKED") return "bad";
  return "muted";
}

/* =========================
   MEDIA QUERY HOOK
   ========================= */
function useMediaQuery(query) {
  const [ok, setOk] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setOk(m.matches);
    onChange();
    if (m.addEventListener) m.addEventListener("change", onChange);
    else m.addListener(onChange);
    return () => {
      if (m.removeEventListener) m.removeEventListener("change", onChange);
      else m.removeListener(onChange);
    };
  }, [query]);

  return ok;
}

/* =========================
   UTILS
   ========================= */

function safeArr(x) {
  return Array.isArray(x) ? x : [];
}

function uaEnum(map, key) {
  if (!key) return "—";
  return map[key] || key;
}
// kept for fallback — label functions in component call this via STATUS_UA/CATEGORY_UA/TYPE_UA

function pad2(n) {
  return String(n).padStart(2, "0");
}

function fmtDT(dt) {
  if (!dt) return "—";
  const d = new Date(String(dt));
  if (!Number.isFinite(d.getTime())) return String(dt);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
}

function fmtDatePill(start, end) {
  const sOk = start ? fmtDT(start) : "—";
  const eOk = end ? fmtDT(end) : "—";
  return `${sOk} → ${eOk}`;
}

function toIsoOrNull(dtLocalStr) {
  if (!dtLocalStr) return null;
  const d = new Date(dtLocalStr);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toISOString();
}

function normalizePhotoList(list) {
  const arr = safeArr(list).filter(
    (p) => typeof p === "string" && p.trim().length > 0
  );
  return arr.filter(
    (p) =>
      p.startsWith("data:image/") ||
      p.startsWith("http://") ||
      p.startsWith("https://") ||
      p.startsWith("/")
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(new Error("FileReader failed"));
    r.readAsDataURL(file);
  });
}

function parseMaybeJson(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function toFeatureCollection(gj) {
  if (!gj) return null;
  if (gj.type === "FeatureCollection") return gj;
  if (gj.type === "Feature")
    return { type: "FeatureCollection", features: [gj] };
  if (gj.type && gj.coordinates) {
    return {
      type: "FeatureCollection",
      features: [{ type: "Feature", properties: {}, geometry: gj }],
    };
  }
  return null;
}

function normalizeLngLat(coord) {
  if (!Array.isArray(coord) || coord.length < 2) return coord;
  const a = Number(coord[0]);
  const b = Number(coord[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return coord;

  const aLooksLat = Math.abs(a) <= 90;
  const bLooksLat = Math.abs(b) <= 90;
  const bLooksLng = Math.abs(b) <= 180;

  if (aLooksLat && bLooksLng && !bLooksLat) return [b, a];
  if (aLooksLat && Math.abs(b) > 90 && Math.abs(a) <= 180) return [b, a];
  return coord;
}

function deepNormalizeCoords(coords) {
  if (!coords) return coords;
  if (typeof coords[0] === "number") return normalizeLngLat(coords);
  return coords.map((c0) => deepNormalizeCoords(c0));
}

function normalizeGeoJSON(gj) {
  const fc = toFeatureCollection(gj);
  if (!fc) return null;

  return {
    type: "FeatureCollection",
    features: safeArr(fc.features).map((f) => {
      if (!f?.geometry?.coordinates) return f;
      return {
        ...f,
        geometry: {
          ...f.geometry,
          coordinates: deepNormalizeCoords(f.geometry.coordinates),
        },
      };
    }),
  };
}

function computeBboxFromGeoJSON(geojson) {
  const coordsList = [];
  function pushCoords(coords) {
    if (!coords) return;
    if (typeof coords[0] === "number" && typeof coords[1] === "number") {
      coordsList.push(coords);
      return;
    }
    for (const c0 of coords) pushCoords(c0);
  }

  const g = geojson;
  if (!g) return null;

  if (g.type === "FeatureCollection") {
    for (const f of safeArr(g.features)) {
      if (f && f.geometry) pushCoords(f.geometry.coordinates);
    }
  } else if (g.type === "Feature") {
    if (g.geometry) pushCoords(g.geometry.coordinates);
  } else if (g.type && g.coordinates) {
    pushCoords(g.coordinates);
  }

  if (coordsList.length === 0) return null;

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  for (const raw of coordsList) {
    const [lng, lat] = normalizeLngLat(raw);
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  if (
    !Number.isFinite(minLng) ||
    !Number.isFinite(minLat) ||
    !Number.isFinite(maxLng) ||
    !Number.isFinite(maxLat)
  ) {
    return null;
  }

  return { minLng, maxLng, minLat, maxLat };
}

function polygonToPreviewSvg(featureCollection, w = 220, h = 128) {
  const fc = featureCollection;
  if (!fc?.features?.length) return null;

  const poly = fc.features.find((f) => f?.geometry?.type === "Polygon");
  if (!poly) return null;

  const coords = poly.geometry.coordinates?.[0];
  if (!Array.isArray(coords) || coords.length < 3) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const raw of coords) {
    const [x, y] = normalizeLngLat(raw);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  const dx = maxX - minX || 1;
  const dy = maxY - minY || 1;

  const pad = 10;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;

  const pts = coords
    .map((raw) => {
      const [x, y] = normalizeLngLat(raw);
      const nx = pad + ((x - minX) / dx) * innerW;
      const ny = pad + (1 - (y - minY) / dy) * innerH;
      return `${nx.toFixed(1)},${ny.toFixed(1)}`;
    })
    .join(" ");

  return `
<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="rgba(40,163,91,0.16)"/>
      <stop offset="1" stop-color="rgba(40,163,91,0.06)"/>
    </linearGradient>
  </defs>
  <rect x="0" y="0" width="${w}" height="${h}" rx="16" fill="url(#bg)" />
  <polygon points="${pts}" fill="rgba(40,163,91,0.12)" stroke="rgba(40,163,91,0.90)" stroke-width="3" />
</svg>`;
}

function getListFromResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.content)) return data.content;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

async function fetchFirstOk(u, navigate, candidates) {
  for (const url of candidates) {
    const r = await apiFetchJson(u, url, { method: "GET" }, navigate);
    if (r && r.ok) return { ok: true, url, data: r.data };
  }
  return { ok: false, url: candidates[0], data: null };
}

function getFieldId(f) {
  const id = Number(f?.id ?? f?.fieldId);
  return Number.isFinite(id) ? id : null;
}

function getFieldName(f) {
  return f?.name || f?.title || f?.fieldName || "Поле";
}

function getFieldGeoRaw(fieldObj) {
  return (
    fieldObj?.geometryJson ||
    fieldObj?.geoJson ||
    fieldObj?.geometry ||
    fieldObj?.polygon ||
    fieldObj?.geom ||
    null
  );
}

/* =========================
   WORKERS: ID detection + labels
   ========================= */

const WORKER_ID_FIELDS = [
  "userId",
  "workerProfileId",
  "profileId",
  "id",
  "workerId",
  "usersId",
  "worker_profile_id",
  "worker_profileId",
];

function numOrNull(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function getWorkerIdByField(w, field) {
  const direct = numOrNull(w?.[field]);
  if (direct != null) return direct;

  const wp = w?.workerProfile || w?.profile || w?.user || null;
  if (wp) {
    const nested = numOrNull(wp?.[field]);
    if (nested != null) return nested;
  }

  return null;
}

function getWorkerFullName(w) {
  const direct =
    w?.fullName ||
    w?.name ||
    w?.displayName ||
    w?.fio ||
    w?.email ||
    w?.username ||
    w?.userName;

  const fromParts = [w?.firstName, w?.lastName].filter(Boolean).join(" ");

  const wp = w?.workerProfile || w?.profile || w?.user || null;
  const nested =
    wp?.fullName ||
    wp?.name ||
    wp?.userName ||
    [wp?.firstName, wp?.lastName].filter(Boolean).join(" ") ||
    wp?.email ||
    wp?.username;

  return direct || fromParts || nested || null;
}

function getWorkerLabel(w) {
  const name = getWorkerFullName(w);
  const uid = getWorkerIdByField(w, "userId");
  if (name && uid != null) return `${name} • userId: ${uid}`;
  if (name) return name;
  if (uid != null) return `Працівник • userId: ${uid}`;
  return "Працівник";
}

function detectWorkerIdMode(workers, tasks) {
  const workersArr = safeArr(workers);
  const taskIds = new Set();

  for (const t of safeArr(tasks)) {
    for (const id of safeArr(t?.workerIds)) {
      const n = numOrNull(id);
      if (n != null) taskIds.add(n);
    }
  }

  if (taskIds.size === 0) {
    const hasUserId = workersArr.some(
      (w) => getWorkerIdByField(w, "userId") != null
    );
    if (hasUserId) return "userId";

    const hasProfileId = workersArr.some(
      (w) =>
        getWorkerIdByField(w, "workerProfileId") != null ||
        getWorkerIdByField(w, "profileId") != null
    );

    if (hasProfileId) {
      return workersArr.some(
        (w) => getWorkerIdByField(w, "workerProfileId") != null
      )
        ? "workerProfileId"
        : "profileId";
    }

    return workersArr.some((w) => getWorkerIdByField(w, "id") != null)
      ? "id"
      : "userId";
  }

  let best = { field: "userId", score: -1 };
  for (const field of WORKER_ID_FIELDS) {
    let score = 0;
    for (const w of workersArr) {
      const wid = getWorkerIdByField(w, field);
      if (wid != null && taskIds.has(wid)) score++;
    }
    if (score > best.score) best = { field, score };
  }

  if (best.score <= 0) {
    const hasUserId = workersArr.some(
      (w) => getWorkerIdByField(w, "userId") != null
    );
    return hasUserId ? "userId" : best.field;
  }

  return best.field;
}

function buildWorkerMaps(workers) {
  const maps = {};
  for (const f of WORKER_ID_FIELDS) maps[f] = new Map();

  for (const w of safeArr(workers)) {
    for (const f of WORKER_ID_FIELDS) {
      const id = getWorkerIdByField(w, f);
      if (id != null && !maps[f].has(id)) maps[f].set(id, w);
    }
  }
  return maps;
}

function findWorkerByAnyId(workerMaps, id) {
  const n = numOrNull(id);
  if (n == null) return null;
  for (const f of WORKER_ID_FIELDS) {
    const w = workerMaps?.[f]?.get(n);
    if (w) return w;
  }
  return null;
}

/* =========================
   MODALS
   ========================= */

function Modal({ open, title, subtitle, onClose, right, children }) {
  const isMob = useMediaQuery("(max-width: 760px)");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const overlayStyle = isMob
    ? { ...ui.overlay, alignItems: "flex-end", padding: 0 }
    : ui.overlay;

  const modalStyle = isMob
    ? {
        ...ui.modal,
        width: "100vw",
        maxHeight: "94vh",
        borderRadius: "18px 18px 0 0",
      }
    : ui.modal;

  return (
    <div style={overlayStyle} onMouseDown={onClose} role="dialog" aria-modal="true">
      <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.modalHeader}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={ui.modalTitle}>{title}</div>
              {subtitle ? <div style={ui.modalSub}>{subtitle}</div> : null}
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {right ? right : null}
              <button onClick={onClose} style={{ ...ui.btn, minHeight: 44, minWidth: 44 }} title="Закрити (Esc)">
                ✕
              </button>
            </div>
          </div>
        </div>
        <div style={ui.modalBody}>{children}</div>
      </div>
    </div>
  );
}

function PhotoLightbox({ open, photos, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, onPrev, onNext]);

  if (!open) return null;
  const list = safeArr(photos);
  const i = Math.max(0, Math.min(list.length - 1, Number(index) || 0));
  const src = list[i];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,.72)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        display: "grid",
        placeItems: "center",
        padding: 14,
      }}
      onMouseDown={onClose}
    >
      <div
        style={{
          width: "min(1080px, 96vw)",
          borderRadius: 18,
          overflow: "hidden",
          border: `1px solid ${c.border}`,
          background: c.modalBg,
          boxShadow: "0 30px 120px rgba(0,0,0,.45)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: 12,
            borderBottom: `1px solid ${c.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            background: `linear-gradient(180deg, ${c.panel2}, ${c.panel})`,
          }}
        >
          <div
            style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
          >
            <span style={ui.chip("muted")}>
              Фото {i + 1}/{list.length}
            </span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={onPrev} style={ui.btn} disabled={list.length <= 1}>
              ←
            </button>
            <button onClick={onNext} style={ui.btn} disabled={list.length <= 1}>
              →
            </button>
            <button onClick={onClose} style={ui.btn} title="Закрити">
              ✕
            </button>
          </div>
        </div>

        <div style={{ padding: 12, display: "grid", placeItems: "center" }}>
          <img
            src={src}
            alt=""
            style={{
              width: "100%",
              maxHeight: "78vh",
              objectFit: "contain",
              borderRadius: 16,
              border: `1px solid ${c.border}`,
              background: c.surfaceSoft,
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* =========================
   DETAILS MAP (readonly)
   ========================= */

function DetailsMap({ open, fieldFc, taskPoly }) {
  const wrapRef = useRef(null);
  const mapRef = useRef(null);

  const FIELD_SRC = "d-field-src";
  const FIELD_FILL = "d-field-fill";
  const FIELD_LINE = "d-field-line";
  const TASK_SRC = "d-task-src";
  const TASK_FILL = "d-task-fill";
  const TASK_LINE = "d-task-line";

  useEffect(() => {
    if (!open) return;

    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) return;
    if (!wrapRef.current) return;

    mapboxgl.accessToken = token;

    try {
      if (mapRef.current) mapRef.current.remove();
    } catch {}
    mapRef.current = null;

    const map = new mapboxgl.Map({
      container: wrapRef.current,
      style: SATELLITE_STYLE,
      center: [31.0, 49.0],
      zoom: 6,
    });
    mapRef.current = map;
    map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      "top-right"
    );

    map.on("load", () => {
      addTerrainLayer(map);
      if (!map.getSource(FIELD_SRC)) {
        map.addSource(FIELD_SRC, {
          type: "geojson",
          data: fieldFc || { type: "FeatureCollection", features: [] },
        });
      }

      if (!map.getLayer(FIELD_FILL)) {
        map.addLayer({
          id: FIELD_FILL,
          type: "fill",
          source: FIELD_SRC,
          paint: { "fill-color": "#22c55e", "fill-opacity": 0.1 },
        });
      }

      if (!map.getLayer(FIELD_LINE)) {
        map.addLayer({
          id: FIELD_LINE,
          type: "line",
          source: FIELD_SRC,
          paint: { "line-color": "#22c55e", "line-width": 4 },
        });
      }

      const taskFc =
        normalizeGeoJSON(toFeatureCollection(taskPoly) ? taskPoly : taskPoly) || {
          type: "FeatureCollection",
          features: [],
        };

      if (!map.getSource(TASK_SRC)) {
        map.addSource(TASK_SRC, { type: "geojson", data: taskFc });
      }

      if (!map.getLayer(TASK_FILL)) {
        map.addLayer({
          id: TASK_FILL,
          type: "fill",
          source: TASK_SRC,
          paint: { "fill-color": "#ef4444", "fill-opacity": 0.22 },
        });
      }

      if (!map.getLayer(TASK_LINE)) {
        map.addLayer({
          id: TASK_LINE,
          type: "line",
          source: TASK_SRC,
          paint: { "line-color": "#ef4444", "line-width": 3 },
        });
      }

      const bboxField = fieldFc ? computeBboxFromGeoJSON(fieldFc) : null;
      const bboxTask = taskPoly
        ? computeBboxFromGeoJSON(toFeatureCollection(taskPoly) || taskPoly)
        : null;
      const bbox = bboxTask || bboxField;

      if (bbox) {
        map.fitBounds(
          [
            [bbox.minLng, bbox.minLat],
            [bbox.maxLng, bbox.maxLat],
          ],
          { padding: 56, duration: 320 }
        );
      }
    });

    return () => {
      try {
        if (mapRef.current) mapRef.current.remove();
      } catch {}
      mapRef.current = null;
    };
  }, [open, fieldFc, taskPoly]);

  const tokenOk = !!process.env.REACT_APP_MAPBOX_TOKEN;

  return (
    <div
      style={{
        borderRadius: 18,
        border: `1px solid ${c.border}`,
        overflow: "hidden",
        background: c.surfaceSoft,
        height: 420,
      }}
    >
      {!tokenOk ? (
        <div style={{ padding: 14 }}>
          <div style={{ fontWeight: 950, color: c.text }}>Немає токена Mapbox</div>
          <div style={{ marginTop: 6, color: c.textDim, fontSize: 12 }}>
            Додай <b>REACT_APP_MAPBOX_TOKEN</b> у .env
          </div>
        </div>
      ) : (
        <div ref={wrapRef} style={{ height: "100%" }} />
      )}
    </div>
  );
}

/* =========================
   FLOATING ACTION BUTTON
   ========================= */

function FloatingActionButton({ onClick, disabled, visible }) {
  const isMobile = useMediaQuery("(max-width: 760px)");

  if (!visible || !isMobile) return null;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        position: "fixed",
        bottom: "calc(66px + env(safe-area-inset-bottom, 0px))",
        right: 18,
        width: 56,
        height: 56,
        borderRadius: 28,
        border: "1px solid var(--primary-btn-border)",
        background: "var(--primary-btn-bg)",
        color: "var(--primary-btn-text)",
        fontSize: 26,
        fontWeight: 950,
        boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
        cursor: disabled ? "not-allowed" : "pointer",
        zIndex: 450,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
        transition: "transform 0.15s ease, opacity 0.15s ease",
        WebkitTapHighlightColor: "transparent",
      }}
      aria-label="Додати задачу"
    >
      +
    </button>
  );
}

/* =========================
   COMPONENT
   ========================= */

export default function Tasks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t: tr } = useTranslation();
  const statusLabel = (k) => k ? (tr(`tasks.status.${k}`, { defaultValue: "" }) || STATUS_UA[k] || k) : "—";
  const categoryLabel = (k) => k ? (tr(`tasks.category.${k}`, { defaultValue: "" }) || CATEGORY_UA[k] || k) : "—";
  const typeLabel = (k) => k ? (tr(`tasks.type.${k}`, { defaultValue: "" }) || TYPE_UA[k] || k) : "—";
  const isNarrow = useMediaQuery("(max-width: 980px)");
  const isMobile = useMediaQuery("(max-width: 760px)");

  const presetWorkerId = location.state?.workerId ?? null;

  const openTaskIdFromNav = useMemo(() => {
    const sp = new URLSearchParams(location.search || "");
    return (
      sp.get("openTaskId") ||
      (location.state?.openTaskId != null
        ? String(location.state.openTaskId)
        : null)
    );
  }, [location.search, location.state]);

  const fieldIdFromQuery = useMemo(() => {
    const sp = new URLSearchParams(location.search || "");
    return sp.get("fieldId");
  }, [location.search]);

  const [user, setUser] = useState(null);
  const isWorker = user?.role === "WORKER";

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [toast, setToast] = useState(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [saving, setSaving] = useState(false);

  const [openDetails, setOpenDetails] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [detailsStatus, setDetailsStatus] = useState("PLANNED");
  const [detailsBusy, setDetailsBusy] = useState(false);

  const [fields, setFields] = useState([]);
  const [workers, setWorkers] = useState([]);

  const [workerIdMode, setWorkerIdMode] = useState("userId");
  const workerMaps = useMemo(() => buildWorkerMaps(workers), [workers]);

  const [fieldQ, setFieldQ] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taskStatus, setTaskStatus] = useState("PLANNED");
  const [workCategory, setWorkCategory] = useState("PLANNING");
  const [workType, setWorkType] = useState("SCOUTING");

  const [plannedStartAt, setPlannedStartAt] = useState("");
  const [plannedEndAt, setPlannedEndAt] = useState("");

  const [fieldId, setFieldId] = useState("");
  const [workerIds, setWorkerIds] = useState([]);

  const [photos, setPhotos] = useState([]);
  const [photoMsg, setPhotoMsg] = useState(null);

  const [geometryJson, setGeometryJson] = useState("");

  const mapWrapRef = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);

  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoList, setPhotoList] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);

  const [q, setQ] = useState("");
  const [filterFieldId, setFilterFieldId] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterWorkerIds, setFilterWorkerIds] = useState([]);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  useEffect(() => {
    if (!fieldIdFromQuery) return;
    const fid = Number(fieldIdFromQuery);
    if (!Number.isFinite(fid)) return;
    setFilterFieldId(String(fid));
  }, [fieldIdFromQuery]);

  const FIELD_SRC = "field-src";
  const FIELD_FILL = "field-fill";
  const FIELD_LINE = "field-line";

  function showToast(type, text) {
    setToast({ type, text });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2600);
  }

  const tasksRef = useRef([]);
  useEffect(() => {
    tasksRef.current = safeArr(tasks);
  }, [tasks]);

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      navigate("/login");
      return;
    }
    setUser(u);
    loadAll(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAll(u) {
    setLoading(true);

    const tasksUrl = u?.role === "WORKER" ? "/api/tasks/worker" : "/api/tasks";
    const tRes = await apiFetchJson(u, tasksUrl, { method: "GET" }, navigate);
    
    if (!tRes.ok) {
      if (u?.role === "WORKER") {
        const altRes = await apiFetchJson(u, "/api/tasks/my", { method: "GET" }, navigate);
        if (altRes.ok) {
          const tList = getListFromResponse(altRes.data);
          setTasks(tList);
        } else {
          setTasks([]);
          showToast("bad", "Не вдалося завантажити задачі");
        }
      } else {
        setTasks([]);
        showToast("bad", tRes.error || `HTTP ${tRes.status}`);
      }
      setLoading(false);
      return;
    }

    const tList = getListFromResponse(tRes.data);
    setTasks(tList);

    const f = await fetchFirstOk(u, navigate, [
      "/api/fields",
      "/api/field",
      "/api/fields/all",
    ]);
    setFields(f.ok ? getListFromResponse(f.data) : []);

    /* WORKERS_DISABLED
    if (!u?.role || u.role !== "WORKER") {
      const w = await fetchFirstOk(u, navigate, [
        "/api/profile/workers?activeOnly=false",
        "/api/profile/workers",
        "/api/workers",
        "/api/worker-profiles",
        "/api/workers/all",
        "/api/workerProfiles",
      ]);
      const wList = w.ok ? getListFromResponse(w.data) : [];
      setWorkers(wList);

      const mode = detectWorkerIdMode(wList, tList);
      setWorkerIdMode(mode);
    } else {
      setWorkers([]);
      setWorkerIdMode("userId");
    }
    */
    setWorkers([]);
    setWorkerIdMode("userId");

    setLoading(false);
  }

  useEffect(() => {
    if (presetWorkerId == null) return;
    if (isWorker) return;

    const wid = numOrNull(presetWorkerId);
    if (wid == null) return;

    setFilterWorkerIds((prev) => (prev.includes(wid) ? prev : [...prev, wid]));

    navigate(location.pathname + location.search, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetWorkerId, isWorker]);

  const workerPrimaryId = (w) => {
    const id = getWorkerIdByField(w, workerIdMode);
    if (id != null) return id;

    for (const f of ["userId", "workerProfileId", "profileId", "id"]) {
      const x = getWorkerIdByField(w, f);
      if (x != null) return x;
    }
    return null;
  };

  const fieldsNormalized = useMemo(() => {
    return safeArr(fields)
      .map((f) => {
        const id = getFieldId(f);
        if (id == null) return null;
        const name = getFieldName(f);
        const raw = getFieldGeoRaw(f);
        const fc = normalizeGeoJSON(parseMaybeJson(raw));
        return { raw: f, id, name, fc };
      })
      .filter(Boolean);
  }, [fields]);

  const fieldsFiltered = useMemo(() => {
    const qq = String(fieldQ || "")
      .trim()
      .toLowerCase();
    if (!qq) return fieldsNormalized;
    return fieldsNormalized.filter((x) =>
      `${x.name} ${x.id}`.toLowerCase().includes(qq)
    );
  }, [fieldsNormalized, fieldQ]);

  const selectedField = useMemo(() => {
    const fid = Number(fieldId);
    if (!Number.isFinite(fid)) return null;
    return fieldsNormalized.find((x) => x.id === fid) || null;
  }, [fieldId, fieldsNormalized]);

  function destroyCreateMap() {
    try {
      if (mapRef.current) mapRef.current.remove();
    } catch {}
    mapRef.current = null;
    drawRef.current = null;
  }

  function setFieldGeoOnCreateMap(fc) {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource(FIELD_SRC);
    if (src) {
      src.setData(fc || { type: "FeatureCollection", features: [] });
    }

    if (fc) {
      const bbox = computeBboxFromGeoJSON(fc);
      if (bbox) {
        map.fitBounds(
          [
            [bbox.minLng, bbox.minLat],
            [bbox.maxLng, bbox.maxLat],
          ],
          { padding: 56, duration: 320 }
        );
      }
    }
  }

  function initCreateMap() {
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) return;
    if (!mapWrapRef.current) return;

    mapboxgl.accessToken = token;
    destroyCreateMap();

    const map = new mapboxgl.Map({
      container: mapWrapRef.current,
      style: SATELLITE_STYLE,
      center: [31.0, 49.0],
      zoom: 5.6,
    });
    mapRef.current = map;

    map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      "top-right"
    );

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      defaultMode: "simple_select",
      styles: [
        {
          id: "gl-draw-polygon-fill",
          type: "fill",
          filter: [
            "all",
            ["==", "$type", "Polygon"],
            ["!=", "mode", "static"],
          ],
          paint: { "fill-color": "#ef4444", "fill-opacity": 0.22 },
        },
        {
          id: "gl-draw-polygon-stroke-active",
          type: "line",
          filter: [
            "all",
            ["==", "$type", "Polygon"],
            ["!=", "mode", "static"],
          ],
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": "#ef4444", "line-width": 3 },
        },
        {
          id: "gl-draw-polygon-and-line-vertex-halo-active",
          type: "circle",
          filter: [
            "all",
            ["==", "meta", "vertex"],
            ["==", "$type", "Point"],
            ["!=", "mode", "static"],
          ],
          paint: { "circle-radius": 7, "circle-color": "#fff" },
        },
        {
          id: "gl-draw-polygon-and-line-vertex-active",
          type: "circle",
          filter: [
            "all",
            ["==", "meta", "vertex"],
            ["==", "$type", "Point"],
            ["!=", "mode", "static"],
          ],
          paint: { "circle-radius": 4, "circle-color": "#ef4444" },
        },
      ],
    });

    drawRef.current = draw;
    map.addControl(draw, "top-left");

    map.on("load", () => {
      addTerrainLayer(map);
      if (!map.getSource(FIELD_SRC)) {
        map.addSource(FIELD_SRC, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
      }

      if (!map.getLayer(FIELD_FILL)) {
        map.addLayer({
          id: FIELD_FILL,
          type: "fill",
          source: FIELD_SRC,
          paint: { "fill-color": "#22c55e", "fill-opacity": 0.1 },
        });
      }

      if (!map.getLayer(FIELD_LINE)) {
        map.addLayer({
          id: FIELD_LINE,
          type: "line",
          source: FIELD_SRC,
          paint: { "line-color": "#22c55e", "line-width": 4 },
        });
      }

      const sync = () => {
        const fc = draw.getAll();
        const poly = safeArr(fc.features).find(
          (f) => f?.geometry?.type === "Polygon"
        );
        setGeometryJson(poly ? JSON.stringify(poly) : "");
      };

      map.on("draw.create", sync);
      map.on("draw.update", sync);
      map.on("draw.delete", sync);

      if (fieldId) {
        const fid = Number(fieldId);
        const f = fieldsNormalized.find((x) => x.id === fid);
        if (f?.fc) setFieldGeoOnCreateMap(f.fc);
      }
    });
  }

  function onChangeField(nextIdStr) {
    setFieldId(nextIdStr);
    const fid = Number(nextIdStr);
    const f = fieldsNormalized.find((x) => x.id === fid);
    if (f?.fc) setFieldGeoOnCreateMap(f.fc);

    setGeometryJson("");
    try {
      if (drawRef.current) drawRef.current.deleteAll();
    } catch {}
  }

  function openCreateModal() {
    if (isWorker) return;

    setOpenCreate(true);
    setSaving(false);

    setTitle("");
    setDescription("");
    setTaskStatus("PLANNED");
    setWorkCategory("PLANNING");
    setWorkType(CATEGORY_TO_TYPES.PLANNING[0]);

    setPlannedStartAt("");
    setPlannedEndAt("");
    setFieldId("");
    setWorkerIds([]);
    setPhotos([]);
    setPhotoMsg(null);
    setGeometryJson("");
    setFieldQ("");

    window.setTimeout(() => initCreateMap(), 0);
  }

  function closeCreateModal() {
    setOpenCreate(false);
    destroyCreateMap();
  }

  function onChangeCategory(nextCat) {
    setWorkCategory(nextCat);
    const allowed = CATEGORY_TO_TYPES[nextCat] || ["OTHER"];
    setWorkType((prev) => (allowed.includes(prev) ? prev : allowed[0]));
  }

  function toggleWorkerById(id) {
    const wid = numOrNull(id);
    if (wid == null) return;
    setWorkerIds((prev) =>
      prev.includes(wid) ? prev.filter((x) => x !== wid) : [...prev, wid]
    );
  }

  async function onPickPhotos(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setPhotoMsg(null);

    try {
      const urls = [];
      for (const f of files) {
        const u = await fileToDataUrl(f);
        if (u) urls.push(u);
      }
      const merged = [...photos, ...urls].slice(0, 12);
      setPhotos(merged);
      if (photos.length + urls.length > 12) {
        setPhotoMsg("Максимум 12 фото. Зайві не додані.");
      }
    } catch {
      setPhotoMsg("Не вдалося додати фото. Спробуй інші файли.");
    } finally {
      e.target.value = "";
    }
  }

  function removePhoto(i) {
    setPhotos((p) => p.filter((_, idx) => idx !== i));
  }

  async function createTask() {
    if (!user || isWorker) return;

    const t = title.trim();
    if (!t) return showToast("bad", "Введи назву задачі");

    const allowedTypes = CATEGORY_TO_TYPES[workCategory] || ["OTHER"];
    if (!allowedTypes.includes(workType)) {
      return showToast("bad", "Невірний тип робіт для категорії");
    }

    const ps = plannedStartAt ? new Date(plannedStartAt).getTime() : null;
    const pe = plannedEndAt ? new Date(plannedEndAt).getTime() : null;
    if (ps && pe && pe < ps) {
      return showToast("bad", "План кінець має бути після старту");
    }

    setSaving(true);

    const body = {
      title: t,
      description: description?.trim() ? description.trim() : null,
      workCategory,
      workType,
      fieldId: fieldId ? Number(fieldId) : null,
      workerIds: safeArr(workerIds),
      plannedStartAt: toIsoOrNull(plannedStartAt),
      plannedEndAt: toIsoOrNull(plannedEndAt),
      photos: safeArr(photos),
    };

    const res = await apiFetchJson(
      user,
      "/api/tasks",
      { method: "POST", body },
      navigate
    );
    if (!res.ok) {
      showToast("bad", res.error || `HTTP ${res.status}`);
      setSaving(false);
      return;
    }

    const created = res.data;

    if (created?.id && taskStatus && taskStatus !== "PLANNED") {
      const stRes = await apiFetchJson(
        user,
        `/api/tasks/${created.id}/status?status=${encodeURIComponent(taskStatus)}`,
        { method: "PATCH" },
        navigate
      );
      if (!stRes.ok) showToast("warn", "Задача створена, але статус не оновився");
    }

    if (created?.id && fieldId && geometryJson) {
      const poly = parseMaybeJson(geometryJson);
      const fc = normalizeGeoJSON(toFeatureCollection(poly) ? poly : poly);
      const bbox = computeBboxFromGeoJSON(fc);

      const gBody = {
        geometryJson,
        bboxMinLng: bbox ? bbox.minLng : null,
        bboxMaxLng: bbox ? bbox.maxLng : null,
        bboxMinLat: bbox ? bbox.minLat : null,
        bboxMaxLat: bbox ? bbox.maxLat : null,
      };

      const gRes = await apiFetchJson(
        user,
        `/api/tasks/${created.id}/geometry`,
        { method: "PUT", body: gBody },
        navigate
      );
      if (!gRes.ok) {
        showToast("warn", "Задача створена, але область не збереглась");
      }
    }

    await loadAll(user);
    showToast("ok", tr("workers.modal.createTitle"));
    setSaving(false);
    closeCreateModal();
  }

  function openDetailsModal(task) {
    if (!task?.id) return;
    setDetailsTask(task);
    setDetailsStatus(task?.taskStatus || "PLANNED");
    setOpenDetails(true);
  }

  function closeDetailsModal() {
    setOpenDetails(false);
    setDetailsTask(null);
    setDetailsBusy(false);

    const sp = new URLSearchParams(location.search || "");
    if (sp.has("openTaskId")) {
      sp.delete("openTaskId");
      const qs = sp.toString();
      navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
        replace: true,
        state: {},
      });
    }
  }

  async function changeDetailsStatus(nextStatus) {
    if (!user || !detailsTask?.id || isWorker) return;
    setDetailsStatus(nextStatus);
  }

  async function saveDetailsStatus() {
    if (!user || !detailsTask?.id || isWorker) return;
    setDetailsBusy(true);

    const res = await apiFetchJson(
      user,
      `/api/tasks/${detailsTask.id}/status?status=${encodeURIComponent(
        detailsStatus
      )}`,
      { method: "PATCH" },
      navigate
    );

    if (!res.ok) {
      showToast("bad", res.error || `HTTP ${res.status}`);
      setDetailsBusy(false);
      return;
    }

    showToast("ok", "Статус оновлено");
    await loadAll(user);

    const fresh =
      safeArr(tasksRef.current).find(
        (t) => String(t?.id) === String(detailsTask.id)
      ) || null;

    setDetailsTask(fresh || detailsTask);
    setDetailsBusy(false);
  }

  async function deleteDetailsTask() {
    if (!user || !detailsTask?.id || isWorker) return;
    const ok = window.confirm(`Видалити задачу #${detailsTask.id}?`);
    if (!ok) return;

    setDetailsBusy(true);
    const res = await apiFetchJson(
      user,
      `/api/tasks/${detailsTask.id}`,
      { method: "DELETE" },
      navigate
    );
    if (!res.ok) {
      showToast("bad", res.error || `HTTP ${res.status}`);
      setDetailsBusy(false);
      return;
    }

    showToast("ok", "Задачу видалено");
    await loadAll(user);
    setDetailsBusy(false);
    closeDetailsModal();
  }

  useEffect(() => {
    if (!openTaskIdFromNav) return;
    if (loading) return;

    const found =
      safeArr(tasks).find(
        (t) => String(t?.id) === String(openTaskIdFromNav)
      ) || null;

    if (found) {
      openDetailsModal(found);
    } else {
      showToast("warn", `Задачу #${openTaskIdFromNav} не знайдено у списку`);
    }

    navigate(location.pathname + location.search, { replace: true, state: {} });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTaskIdFromNav, loading, tasks]);

  const tasksFiltered = useMemo(() => {
    let list = safeArr(tasks);

    const qq = String(q || "")
      .trim()
      .toLowerCase();
    if (qq) {
      list = list.filter((t) => {
        const hay = [
          t?.title,
          t?.description,
          statusLabel(t?.taskStatus),
          categoryLabel(t?.workCategory),
          typeLabel(t?.workType),
          String(t?.id ?? ""),
          String(t?.fieldId ?? ""),
        ]
          .filter(Boolean)
          .join(" | ")
          .toLowerCase();

        return hay.includes(qq);
      });
    }

    if (filterStatus && filterStatus !== "ALL") {
      list = list.filter(
        (t) => String(t?.taskStatus || "PLANNED") === String(filterStatus)
      );
    }

    if (filterFieldId && filterFieldId !== "ALL") {
      const fid = Number(filterFieldId);
      if (Number.isFinite(fid)) {
        list = list.filter((t) => Number(t?.fieldId) === fid);
      }
    }

    if (!isWorker && safeArr(filterWorkerIds).length) {
      const need = new Set(
        filterWorkerIds.map((x) => numOrNull(x)).filter((x) => x != null)
      );
      list = list.filter((t) => {
        const ids = safeArr(t?.workerIds)
          .map((x) => numOrNull(x))
          .filter((x) => x != null);
        return ids.some((id) => need.has(id));
      });
    }

    const fromMs = filterFrom ? new Date(filterFrom).getTime() : null;
    const toMs = filterTo ? new Date(filterTo).getTime() : null;

    if (fromMs || toMs) {
      list = list.filter((t) => {
        const s = t?.plannedStartAt
          ? new Date(String(t.plannedStartAt)).getTime()
          : null;
        const e = t?.plannedEndAt
          ? new Date(String(t.plannedEndAt)).getTime()
          : null;

        const any = s || e;
        if (!any) return false;

        const left = s ?? e ?? null;
        const right = e ?? s ?? null;

        if (fromMs && right != null && right < fromMs) return false;
        if (toMs && left != null && left > toMs) return false;
        return true;
      });
    }

    return list;
  }, [
    tasks,
    q,
    filterFieldId,
    filterStatus,
    filterWorkerIds,
    filterFrom,
    filterTo,
    isWorker,
  ]);

  const allowedTypesForCategory = useMemo(
    () => CATEGORY_TO_TYPES[workCategory] || ["OTHER"],
    [workCategory]
  );

  const headerRight = (
    <div
      style={{ 
        display: "flex", 
        gap: isMobile ? 6 : 10, 
        alignItems: "center", 
        flexWrap: "wrap",
        width: isMobile ? "100%" : "auto",
        justifyContent: isMobile ? "space-between" : "flex-end",
      }}
    >
      <button 
        onClick={() => loadAll(user)} 
        style={{
          ...ui.btn,
          fontSize: isMobile ? 13 : 14,
          padding: isMobile ? "0 8px" : "0 12px",
        }} 
        title="Оновити"
      >
        {isMobile ? "⟳" : "⟳ Оновити"}
      </button>

      {!isWorker && !isMobile ? (
        <button 
          onClick={openCreateModal} 
          style={{
            ...ui.btnPrimary,
            fontSize: isMobile ? 13 : 14,
            padding: isMobile ? "0 8px" : "0 12px",
            flex: isMobile ? 1 : "none",
          }}
        >
          + Додати
        </button>
      ) : null}
    </div>
  );

  const createLayout = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1.05fr .95fr",
    gap: isMobile ? 10 : 14,
    alignItems: "start",
  };

  function resetFilters() {
    setQ("");
    setFilterFieldId("ALL");
    setFilterStatus("ALL");
    setFilterWorkerIds([]);
    setFilterFrom("");
    setFilterTo("");
    showToast("ok", "Фільтри скинуто");
  }

  function openPhotoViewer(list, i = 0) {
    const photosOk = normalizePhotoList(list);
    if (!photosOk.length) return;
    setPhotoList(photosOk);
    setPhotoIndex(Math.max(0, Math.min(photosOk.length - 1, i)));
    setPhotoOpen(true);
  }

  const detailsField = useMemo(() => {
    const t = detailsTask;
    if (!t?.fieldId) return null;
    return fieldsNormalized.find((x) => x.id === Number(t.fieldId)) || null;
  }, [detailsTask, fieldsNormalized]);

  const detailsWorkers = useMemo(() => {
    const t = detailsTask;
    const ids = safeArr(t?.workerIds)
      .map((x) => numOrNull(x))
      .filter((x) => x != null);
    return ids.map((id) => ({ id, w: findWorkerByAnyId(workerMaps, id) }));
  }, [detailsTask, workerMaps]);

  const detailsTaskPoly = useMemo(
    () => parseMaybeJson(detailsTask?.geometryJson),
    [detailsTask]
  );

  return (
    <AppShell
      title={tr("tasks.title")}
      subtitle={
        loading
          ? tr("common.loading")
          : isMobile
          ? `${tasksFiltered.length}/${safeArr(tasks).length}`
          : `${tr("workers.found")}: ${tasksFiltered.length}/${safeArr(tasks).length}`
      }
      right={headerRight}
    >
      {toast ? (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 14,
            ...pillStyle(toast.type),
          }}
        >
          <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 950 }}>{toast.text}</div>
        </div>
      ) : null}

      {!loading ? (
        <div style={{ ...ui.panel, marginTop: 14, padding: isMobile ? 10 : 14 }}>
          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontWeight: 950, color: c.text, fontSize: isMobile ? 14 : 16 }}>
                Фільтри
              </div>
              <button onClick={resetFilters} style={ui.btn} title="Скинути">
                ↺ {isMobile ? "" : "Скинути"}
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1.2fr .8fr .8fr",
                gap: isMobile ? 10 : 12,
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                  Пошук
                </div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Назва/опис"
                  style={ui.input}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                  Статус
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={ui.input}
                >
                  <option value="ALL">Усі</option>
                  {Object.keys(STATUS_UA).map((k) => (
                    <option key={k} value={k}>
                      {statusLabel(k)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                  Поле
                </div>
                <select
                  value={filterFieldId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFilterFieldId(val);

                    const sp = new URLSearchParams(location.search || "");

                    if (val === "ALL") {
                      sp.delete("fieldId");
                    } else {
                      sp.set("fieldId", val);
                    }

                    const qs = sp.toString();
                    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
                      replace: true,
                    });
                  }}
                  style={ui.input}
                >
                  <option value="ALL">Усі</option>
                  {fieldsNormalized.map((f) => (
                    <option key={f.id} value={String(f.id)}>
                      {isMobile ? `${f.name}` : `${f.name} • ${f.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : !isWorker
                  ? "1fr 1fr 1fr"
                  : "1fr 1fr",
                gap: isMobile ? 10 : 12,
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                  Період: від
                </div>
                <input
                  type="datetime-local"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  style={ui.input}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                  Період: до
                </div>
                <input
                  type="datetime-local"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  style={ui.input}
                />
              </div>

              {/* WORKERS_DISABLED: worker filter desktop */}
            </div>

            {/* WORKERS_DISABLED: worker filter mobile */}

            {/* WORKERS_DISABLED: filter chips */}
          </div>
        </div>
      ) : null}

      {!loading && tasksFiltered.length === 0 ? (
        <div style={{ ...ui.panel, marginTop: 14 }}>
          <div style={{ fontWeight: 950, color: c.text, fontSize: isMobile ? 14 : 16 }}>
            Немає задач
          </div>
          <div style={{ marginTop: 6, color: c.textDim, fontSize: isMobile ? 12 : 12 }}>
            Спробуй скинути фільтри.
          </div>
        </div>
      ) : null}

      {!loading && tasksFiltered.length > 0 ? (
        <div style={{ ...ui.panel, marginTop: 14, padding: isMobile ? 10 : 14 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontWeight: 950, color: c.text, fontSize: isMobile ? 14 : 16 }}>
                Список задач
              </div>
              <div style={{ marginTop: 6, color: c.textDim, fontSize: isMobile ? 11 : 12 }}>
                Показано: {tasksFiltered.length}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(330px, 1fr))",
              gap: isMobile ? 10 : 12,
            }}
          >
            {tasksFiltered.map((t) => {
              const st = t.taskStatus || "PLANNED";
              const tone = statusTone(st);

              const field =
                t.fieldId != null
                  ? fieldsNormalized.find((x) => x.id === Number(t.fieldId))
                  : null;

              const fieldTitle = field
                ? isMobile
                  ? `${field.name}`
                  : `${field.name} • ID ${field.id}`
                : t.fieldId != null
                ? `Поле • ID ${t.fieldId}`
                : "Без поля";

              const photosOk = normalizePhotoList(t.photos).slice(0, 3);

              // WORKERS_DISABLED: const workerChips = [];
              const workerChips = [];

              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openDetailsModal(t)}
                  style={{
                    textAlign: "left",
                    border: `1px solid ${c.border}`,
                    borderRadius: 18,
                    padding: isMobile ? 12 : 14,
                    background: `linear-gradient(180deg, ${c.panel2}, ${c.panel})`,
                    cursor: "pointer",
                    color: c.text,
                    boxShadow: c.shadowSoft,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 950,
                          fontSize: isMobile ? 15 : 16,
                          lineHeight: 1.2,
                          wordBreak: "break-word",
                        }}
                      >
                        {t.title || "Без назви"}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <span style={ui.chip(tone)}>
                          {statusLabel(st)}
                        </span>
                        {!isMobile && (
                          <span style={ui.chip("muted")}>
                            {categoryLabel(t.workCategory)}
                          </span>
                        )}
                        <span style={ui.chip("muted")}>
                          {typeLabel(t.workType)}
                        </span>
                        {t.geometryJson && !isMobile && (
                          <span style={ui.chip("bad")}>Є область</span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "grid", justifyItems: "end", gap: 8 }}>
                      <span style={ui.chip("muted")}>#{t.id}</span>
                    </div>
                  </div>

                  <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <span style={ui.chip("muted")}>
                        🌿 {fieldTitle}
                      </span>
                      {!isMobile && (
                        <span style={ui.chip("muted")}>
                          🗓️ {fmtDatePill(t.plannedStartAt, t.plannedEndAt)}
                        </span>
                      )}
                    </div>

                    {isMobile && (
                      <span style={ui.chip("muted")}>
                        🗓️ {fmtDatePill(t.plannedStartAt, t.plannedEndAt)}
                      </span>
                    )}

                    {t.description ? (
                      <div style={{ color: c.text, lineHeight: 1.4, fontSize: isMobile ? 13 : 14 }}>
                        {String(t.description).slice(0, isMobile ? 80 : 150)}
                        {String(t.description).length > (isMobile ? 80 : 150) ? "…" : ""}
                      </div>
                    ) : (
                      <div style={{ color: c.textDim, fontSize: 12 }}>Опис: —</div>
                    )}

                    {/* WORKERS_DISABLED: workerChips render */}

                    {photosOk.length > 0 ? (
                      <div
                        style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {photosOk.map((p, i) => (
                          <img
                            key={i}
                            src={p}
                            alt=""
                            onClick={() => openPhotoViewer(t.photos, i)}
                            style={{
                              width: isMobile ? 70 : 92,
                              height: isMobile ? 70 : 92,
                              objectFit: "cover",
                              borderRadius: 16,
                              border: `1px solid ${c.border}`,
                              background: c.surfaceSoft,
                              cursor: "pointer",
                            }}
                            title="Відкрити"
                          />
                        ))}
                      </div>
                    ) : !isMobile ? (
                      <div style={{ display: "flex", gap: 10 }}>
                        <div
                          style={{
                            width: 92,
                            height: 92,
                            borderRadius: 16,
                            border: `1px dashed ${c.border}`,
                            background: c.surfaceSoft,
                          }}
                        />
                        <div
                          style={{
                            width: 92,
                            height: 92,
                            borderRadius: 16,
                            border: `1px dashed ${c.border}`,
                            background: c.surfaceSoft,
                          }}
                        />
                        <div
                          style={{
                            width: 92,
                            height: 92,
                            borderRadius: 16,
                            border: `1px dashed ${c.border}`,
                            background: c.surfaceSoft,
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Floating Action Button для мобільних */}
      <FloatingActionButton
        onClick={openCreateModal}
        disabled={saving || isWorker}
        visible={!isWorker && isMobile}
      />

      <Modal
        open={openDetails}
        title={detailsTask?.title ? (isMobile ? detailsTask.title : `Задача: ${detailsTask.title}`) : "Деталі задачі"}
        subtitle={
          detailsTask?.id
            ? isMobile
              ? `ID: ${detailsTask.id}`
              : `ID: ${detailsTask.id}`
            : ""
        }
        onClose={closeDetailsModal}
        right={
          !isWorker ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={saveDetailsStatus}
                disabled={detailsBusy || !detailsTask?.id}
                style={
                  detailsBusy
                    ? { ...ui.btnPrimary, opacity: 0.7, cursor: "not-allowed", fontSize: isMobile ? 13 : 14 }
                    : { ...ui.btnPrimary, fontSize: isMobile ? 13 : 14 }
                }
                title="Зберегти статус"
              >
                {detailsBusy ? (isMobile ? "…" : "Збереження…") : isMobile ? "Збер." : "Зберегти статус"}
              </button>
              <button
                onClick={deleteDetailsTask}
                disabled={detailsBusy || !detailsTask?.id}
                style={
                  detailsBusy
                    ? { ...ui.btnDanger, opacity: 0.7, cursor: "not-allowed", fontSize: isMobile ? 13 : 14 }
                    : { ...ui.btnDanger, fontSize: isMobile ? 13 : 14 }
                }
                title="Видалити задачу"
              >
                {isMobile ? "🗑" : "🗑 Видалити"}
              </button>
            </div>
          ) : null
        }
      >
        {!detailsTask ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: c.textDim }}>Немає даних</div>
        ) : (() => {
          const dst = detailsTask.taskStatus || "PLANNED";
          const dMeta = DETAILS_STATUS_META[dst] || DETAILS_STATUS_META.PLANNED;
          const dPhotos = normalizePhotoList(detailsTask.photos);
          const dFieldName = detailsField
            ? detailsField.name || `Поле #${detailsField.id}`
            : detailsTask.fieldId != null ? `Поле #${detailsTask.fieldId}` : null;
          const dStart = detailsTask.plannedStartAt
            ? new Date(detailsTask.plannedStartAt).toLocaleDateString("uk-UA", { day:"2-digit", month:"short", year:"numeric" })
            : null;
          const dEnd = detailsTask.plannedEndAt
            ? new Date(detailsTask.plannedEndAt).toLocaleDateString("uk-UA", { day:"2-digit", month:"short", year:"numeric" })
            : null;

          return (
            <div style={{ display: "grid", gap: 14 }}>

              {/* Hero */}
              <div style={{
                borderRadius: 20, padding: "20px 22px",
                background: `linear-gradient(135deg, ${dMeta.color}1a 0%, rgba(0,0,0,0) 70%)`,
                border: `1px solid ${dMeta.border}`,
                boxShadow: `0 0 40px ${dMeta.color}12`,
                display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, padding: "4px 11px", borderRadius: 99, background: dMeta.bg, border: `1px solid ${dMeta.border}`, color: dMeta.color, textTransform: "uppercase", letterSpacing: ".05em" }}>
                      {dMeta.icon} {dMeta.label}
                    </span>
                    {detailsTask.workCategory && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 99, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.5)" }}>
                        {categoryLabel(detailsTask.workCategory)}
                      </span>
                    )}
                    {detailsTask.workType && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 99, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.5)" }}>
                        {typeLabel(detailsTask.workType)}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "clamp(16px,3vw,22px)", fontWeight: 950, color: "rgba(255,255,255,.92)", wordBreak: "break-word", lineHeight: 1.25 }}>
                    {detailsTask.title || "Задача"}
                  </div>
                  <div style={{ marginTop: 10, display: "flex", gap: 14, flexWrap: "wrap" }}>
                    {dFieldName && <span style={{ fontSize: 12, color: "rgba(255,255,255,.45)", fontWeight: 700 }}>🌿 {dFieldName}</span>}
                    {dStart && <span style={{ fontSize: 12, color: "rgba(255,255,255,.45)", fontWeight: 700 }}>🗓 {dStart}{dEnd && ` → ${dEnd}`}</span>}
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,.25)", fontWeight: 700 }}>#{detailsTask.id}</span>
                  </div>
                </div>
                <div style={{ fontSize: 44, opacity: .2, userSelect: "none", flexShrink: 0 }}>{dMeta.icon}</div>
              </div>

              {/* 2-col */}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, alignItems: "start" }}>

                {/* LEFT */}
                <div style={{ display: "grid", gap: 12 }}>

                  {/* Status */}
                  {!isWorker && (
                    <div style={{ background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 18, padding: "16px 18px" }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 12 }}>Змінити статус</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {Object.keys(DETAILS_STATUS_META).map(k => {
                          const m = DETAILS_STATUS_META[k];
                          const active = detailsStatus === k;
                          return (
                            <button key={k} type="button"
                              onClick={() => changeDetailsStatus(k)}
                              disabled={detailsBusy}
                              style={{
                                display: "flex", alignItems: "center", gap: 6, padding: "7px 13px",
                                borderRadius: 11, cursor: detailsBusy ? "default" : "pointer", fontWeight: 700, fontSize: 12,
                                border: active ? `1.5px solid ${m.border}` : "1.5px solid rgba(255,255,255,.07)",
                                background: active ? m.bg : "rgba(255,255,255,.03)",
                                color: active ? m.color : "rgba(255,255,255,.35)",
                                boxShadow: active ? `0 0 10px ${m.color}22` : "none",
                                transition: "all .12s",
                              }}>
                              {m.icon} {m.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Info */}
                  <div style={{ background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 18, padding: "16px 18px" }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 10 }}>Деталі</div>
                    {[
                      ["🏷", "Категорія", categoryLabel(detailsTask.workCategory)],
                      ["🔧", "Тип роботи", typeLabel(detailsTask.workType)],
                      dFieldName ? ["🌿", "Поле", dFieldName] : null,
                      dStart ? ["▶", "Початок", dStart] : null,
                      dEnd   ? ["⏹", "Кінець",  dEnd]   : null,
                    ].filter(Boolean).map(([icon, label, value]) => (
                      <div key={label} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,.04)" }}>
                        <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,.8)" }}>{value || "—"}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Description */}
                  <div style={{ background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 18, padding: "16px 18px" }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 10 }}>Опис</div>
                    {detailsTask.description ? (
                      <div style={{ fontSize: 13, lineHeight: 1.65, color: "rgba(255,255,255,.7)", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 160, overflowY: "auto" }}>
                        {String(detailsTask.description)}
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,.2)", fontStyle: "italic" }}>Опис не вказано</div>
                    )}
                  </div>

                  {/* Photos */}
                  {dPhotos.length > 0 && (
                    <div style={{ background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 18, padding: "16px 18px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: ".08em" }}>Фото</div>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 99, background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.2)", color: "#4ade80" }}>{dPhotos.length} шт.</span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px,1fr))", gap: 8 }}>
                        {dPhotos.map((p, i) => (
                          <div key={i} onClick={() => openPhotoViewer(detailsTask.photos, i)} style={{ aspectRatio: "1", borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,255,255,.08)", cursor: "pointer", transition: "transform .15s" }}
                            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.04)"}
                            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                            <img src={p} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* RIGHT — Map */}
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 18, overflow: "hidden" }}>
                    <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 2 }}>Карта</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,.25)", fontWeight: 600 }}>
                        {dFieldName ? `🟢 ${dFieldName}` : "Поле не задане"}
                        {detailsTaskPoly ? " · 🔴 Зона задачі" : ""}
                      </div>
                    </div>
                    <DetailsMap
                      open={openDetails}
                      fieldFc={detailsField?.fc || null}
                      taskPoly={detailsTaskPoly || null}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      {!isWorker ? (
        <Modal
          open={openCreate}
          title={isMobile ? "Нова задача" : "Нова задача"}
          subtitle={null}
          onClose={closeCreateModal}
          right={
            !isMobile ? (
              <button
                onClick={createTask}
                disabled={saving}
                style={
                  saving
                    ? { ...ui.btnPrimary, opacity: 0.7, cursor: "not-allowed", fontSize: isMobile ? 13 : 14 }
                    : { ...ui.btnPrimary, fontSize: isMobile ? 13 : 14 }
                }
              >
                {saving ? (isMobile ? "…" : "Збереження…") : isMobile ? "Зберегти" : "Зберегти"}
              </button>
            ) : null
          }
        >
          <div style={createLayout}>
            <div style={{ display: "grid", gap: isMobile ? 10 : 12 }}>
              <div style={ui.card}>
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>Назва *</div>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Назва задачі"
                      style={ui.input}
                    />
                  </div>

                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>Опис</div>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Деталі задачі…"
                      style={{ ...ui.textarea, minHeight: isMobile ? 80 : 110 }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>Статус</div>
                      <select
                        value={taskStatus}
                        onChange={(e) => setTaskStatus(e.target.value)}
                        style={ui.input}
                      >
                        {Object.keys(STATUS_UA).map((k) => (
                          <option key={k} value={k}>
                            {statusLabel(k)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                        Категорія *
                      </div>
                      <select
                        value={workCategory}
                        onChange={(e) => onChangeCategory(e.target.value)}
                        style={ui.input}
                      >
                        {Object.keys(CATEGORY_UA).map((k) => (
                          <option key={k} value={k}>
                            {categoryLabel(k)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                        Тип робіт *
                      </div>
                      <select
                        value={workType}
                        onChange={(e) => setWorkType(e.target.value)}
                        style={ui.input}
                      >
                        {allowedTypesForCategory.map((k) => (
                          <option key={k} value={k}>
                            {typeLabel(k)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                        План старт
                      </div>
                      <input
                        type="datetime-local"
                        value={plannedStartAt}
                        onChange={(e) => setPlannedStartAt(e.target.value)}
                        style={ui.input}
                      />
                    </div>
                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: c.textDim }}>
                        План кінець
                      </div>
                      <input
                        type="datetime-local"
                        value={plannedEndAt}
                        onChange={(e) => setPlannedEndAt(e.target.value)}
                        style={ui.input}
                      />
                    </div>
                  </div>

                  {isMobile && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 11 }}>
                      <span style={ui.chip("muted")}>
                        {plannedStartAt || plannedEndAt
                          ? fmtDatePill(toIsoOrNull(plannedStartAt), toIsoOrNull(plannedEndAt))
                          : "Дати не вибрані"}
                      </span>
                      <span style={ui.chip("muted")}>
                        Поле: {selectedField ? selectedField.name : fieldId ? `ID ${fieldId}` : "не вибрано"}
                      </span>
                      <span style={ui.chip(workerIds.length ? "ok" : "muted")}>
                        👤 {workerIds.length}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div style={ui.card}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 10,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 950, color: c.text }}>Поле</div>
                    {!isMobile && (
                      <div style={{ marginTop: 6, color: c.textDim, fontSize: 12 }}>
                        {fieldsNormalized.length
                          ? `${fieldsFiltered.length}/${fieldsNormalized.length}`
                          : "—"}
                      </div>
                    )}
                  </div>
                  <span style={ui.chip(selectedField?.fc ? "ok" : "muted")}>
                    {selectedField?.fc ? "OK" : "—"}
                  </span>
                </div>

                <div style={{ marginTop: 10 }}>
                  <input
                    value={fieldQ}
                    onChange={(e) => setFieldQ(e.target.value)}
                    placeholder="Пошук поля…"
                    style={ui.input}
                  />
                </div>

                <div
                  style={{
                    marginTop: 10,
                    border: `1px solid ${c.border}`,
                    borderRadius: 16,
                    background: c.surfaceSoft,
                    padding: 10,
                    maxHeight: isMobile ? 200 : 280,
                    overflow: "auto",
                  }}
                >
                  {fieldsFiltered.length === 0 ? (
                    <div style={{ color: c.textDim, fontSize: 13, padding: 8 }}>
                      Нічого не знайдено.
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                        gap: 10,
                      }}
                    >
                      {fieldsFiltered.map((f) => {
                        const selected = fieldId && Number(fieldId) === f.id;
                        const svg = f.fc
                          ? polygonToPreviewSvg(
                              f.fc,
                              isMobile ? 400 : 220,
                              isMobile ? 120 : 128
                            )
                          : null;

                        return (
                          <button
                            key={String(f.id)}
                            type="button"
                            onClick={() => onChangeField(String(f.id))}
                            style={{
                              borderRadius: 16,
                              border: selected
                                ? "1px solid color-mix(in srgb, var(--g) 34%, transparent)"
                                : `1px solid ${c.border}`,
                              background: selected
                                ? "color-mix(in srgb, var(--g) 10%, var(--surface))"
                                : `linear-gradient(180deg, ${c.panel2}, ${c.panel})`,
                              padding: 10,
                              cursor: "pointer",
                              color: c.text,
                              textAlign: "left",
                              boxShadow: selected ? c.shadowSoft : "none",
                            }}
                          >
                            <div style={{ borderRadius: 14, overflow: "hidden" }}>
                              {svg ? (
                                <div dangerouslySetInnerHTML={{ __html: svg }} />
                              ) : (
                                <div
                                  style={{
                                    height: isMobile ? 120 : 128,
                                    borderRadius: 14,
                                    border: `1px dashed ${c.border}`,
                                    display: "grid",
                                    placeItems: "center",
                                    color: c.textDim,
                                    fontWeight: 850,
                                    fontSize: 12,
                                    background: c.surfaceSoft,
                                  }}
                                >
                                  Немає геометрії
                                </div>
                              )}
                            </div>

                            <div
                              style={{
                                marginTop: 8,
                                fontWeight: 950,
                                fontSize: isMobile ? 13 : 13,
                              }}
                            >
                              {f.name} {!isMobile && `• ID: ${f.id}`}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

                            {/* WORKERS_DISABLED: create modal workers card */}

              <div style={ui.card}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    alignItems: "baseline",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 950, color: c.text }}>Фото</div>
                    {!isMobile && (
                      <div style={{ marginTop: 6, color: c.textDim, fontSize: 12 }}>
                        До 12 фото
                      </div>
                    )}
                  </div>
                  <span style={ui.chip(photos.length ? "ok" : "muted")}>
                    📷 {photos.length}
                  </span>
                </div>

                <div style={{ marginTop: 10 }}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onPickPhotos}
                    style={{ color: c.text, fontSize: isMobile ? 12 : 14 }}
                  />
                  {photoMsg ? (
                    <div
                      style={{
                        marginTop: 8,
                        ...pillStyle("warn"),
                        padding: 10,
                        borderRadius: 14,
                        fontSize: 13,
                        fontWeight: 900,
                      }}
                    >
                      {photoMsg}
                    </div>
                  ) : null}
                </div>

                {photos.length > 0 ? (
                  <div
                    style={{
                      marginTop: 12,
                      display: "grid",
                      gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fit, minmax(110px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {photos.map((p, i) => (
                      <div key={i} style={{ position: "relative" }}>
                        <img
                          src={p}
                          alt=""
                          onClick={() => openPhotoViewer(photos, i)}
                          style={{
                            width: "100%",
                            height: isMobile ? 100 : 120,
                            objectFit: "cover",
                            borderRadius: 16,
                            border: `1px solid ${c.border}`,
                            background: c.surfaceSoft,
                            cursor: "pointer",
                          }}
                          title="Відкрити"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          title="Видалити"
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 26,
                            height: 26,
                            borderRadius: 999,
                            border: `1px solid ${c.borderStrong}`,
                            background: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            cursor: "pointer",
                            fontWeight: 950,
                            fontSize: 14,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* Мобільні кнопки в модалці */}
              {isMobile && (
                <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 10 }}>
                  <button onClick={closeCreateModal} disabled={saving} style={{ ...ui.btn, flex: 1 }}>
                    Скасувати
                  </button>
                  <button
                    onClick={createTask}
                    disabled={saving}
                    style={
                      saving
                        ? { ...ui.btnPrimary, opacity: 0.7, cursor: "not-allowed", flex: 2 }
                        : { ...ui.btnPrimary, flex: 2 }
                    }
                  >
                    {saving ? "…" : "Зберегти"}
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: "grid", gap: isMobile ? 10 : 12 }}>
              <div style={ui.card}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    alignItems: "baseline",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 950, color: c.text }}>Мапа задачі</div>
                    {!isMobile && (
                      <div style={{ marginTop: 6, color: c.textDim, fontSize: 12 }}>
                        Поле — зелений • Проблема — червоний
                      </div>
                    )}
                  </div>
                  <span style={ui.chip("muted")}>Polygon</span>
                </div>
              </div>

              {!process.env.REACT_APP_MAPBOX_TOKEN ? (
                <div
                  style={{
                    ...ui.card,
                    border: "1px solid color-mix(in srgb, var(--warn) 28%, transparent)",
                    background: "color-mix(in srgb, var(--warn) 10%, var(--panel))",
                  }}
                >
                  <div style={{ fontWeight: 950, color: c.text }}>
                    Немає токена Mapbox
                  </div>
                  <div style={{ marginTop: 6, color: c.textDim, fontSize: 12 }}>
                    Додай <b>REACT_APP_MAPBOX_TOKEN</b> у .env
                  </div>
                </div>
              ) : (
                <div
                  ref={mapWrapRef}
                  style={{
                    height: isMobile ? 300 : 560,
                    borderRadius: 18,
                    border: `1px solid ${c.border}`,
                    overflow: "hidden",
                    background: c.surfaceSoft,
                  }}
                />
              )}
            </div>
          </div>
        </Modal>
      ) : null}

      <PhotoLightbox
        open={photoOpen}
        photos={photoList}
        index={photoIndex}
        onClose={() => setPhotoOpen(false)}
        onPrev={() =>
          setPhotoIndex((i) =>
            photoList.length ? (i - 1 + photoList.length) % photoList.length : 0
          )
        }
        onNext={() =>
          setPhotoIndex((i) =>
            photoList.length ? (i + 1) % photoList.length : 0
          )
        }
      />
    </AppShell>
  );
}